// Shared "Certificate of Completion" block stamped onto every legal-doc PDF
// (TOU / BAA / PSA, plus the NAF audit appendix). DocuSign-style: shows the
// legally-required attestation a recipient needs (signer identity, intent,
// authentication, timestamps, document hash, signature ID) and intentionally
// omits IP / device / geo / user-agent — those live in the backend audit
// record behind the signature ID for forensic / compliance access only.
//
// Capture vs. display intentionally diverge here. The full ESIGN audit
// record (deviceMetadata, selfie, pin hash) is persisted server-side at
// sign-time; this PDF block surfaces only what a counterparty needs to read.

import { Document, Page, Text, View, StyleSheet, pdf } from "@react-pdf/renderer";
import { PDFDocument } from "pdf-lib";
import moment from "moment";

// Short deterministic visible audit tag derived from the signer + signed
// timestamp. NOT a cryptographic identifier — anyone with the seed can
// recompute it. Purpose: a stable, citeable token ("SIG-ID 3X9FKQ") an
// auditor can cross-reference against the backend audit log.
//
// Identical algorithm previously lived at ModalNewAccountForm.js#shortSignatureId
// — moved here so NAF in-PDF stamps and the certificate produce the same
// ID for the same {signedBy, signedDate}.
export function shortSignatureId(sigInfo) {
  const seed = `${sigInfo?.signedBy || ""}|${sigInfo?.signedDate || ""}`;
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return Math.abs(h).toString(36).padStart(6, "0").slice(0, 8).toUpperCase();
}

// Returns { utc, local } strings for the dual-timestamp rows.
// utc is always present; local degrades to null when timeZone is unknown
// (older signed records that pre-date deviceMetadata capture).
export function formatUtcAndLocal(iso, timeZone) {
  if (!iso) return { utc: null, local: null };
  const m = moment(iso);
  if (!m.isValid()) return { utc: null, local: null };
  const utc = `${m.utc().format("YYYY-MM-DD HH:mm:ss")} UTC`;
  let local = null;
  if (timeZone) {
    try {
      const localM = m.clone();
      const local24 = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short",
      }).format(localM.toDate());
      local = `${local24}  (${timeZone})`;
    } catch {
      local = null;
    }
  }
  return { utc, local };
}

// Human-readable elapsed time between consent acceptance and signature
// completion. Useful context — a signature placed 200ms after accepting
// consent reads differently than one placed 30 seconds after.
function formatElapsed(fromIso, toIso) {
  if (!fromIso || !toIso) return null;
  const ms = moment(toIso).diff(moment(fromIso));
  if (!Number.isFinite(ms) || ms < 0) return null;
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s before signing`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return s === 0 ? `${m}m before signing` : `${m}m ${s}s before signing`;
}

// SHA-256 of a string, returned as lowercase hex. Browser crypto.subtle is
// the only consumer (PDF gen runs in the browser). No new dep.
export async function computeContentHash(input) {
  if (typeof input !== "string" || input.length === 0) return null;
  try {
    const data = new TextEncoder().encode(input);
    const buf = await crypto.subtle.digest("SHA-256", data);
    const bytes = Array.from(new Uint8Array(buf));
    return bytes.map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}

// Visible elision of the long hash so the certificate stays readable while
// the full hash remains verifiable in the printed PDF text layer (a verifier
// can copy the visible head/tail and confirm vs. a recomputed hash).
function abbreviateHash(hex) {
  if (!hex || hex.length < 16) return hex || "—";
  return `${hex.slice(0, 12)}…${hex.slice(-4)}`;
}

const styles = StyleSheet.create({
  cert: {
    marginTop: 32,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: "#1f4f8a",
    borderBottomWidth: 1,
    borderBottomColor: "#1f4f8a",
    backgroundColor: "#f4f7fb",
  },
  title: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 1.2,
    color: "#1f4f8a",
    marginBottom: 10,
    textAlign: "center",
  },
  row: {
    flexDirection: "row",
    marginBottom: 4,
  },
  label: {
    width: 110,
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: "#33415c",
  },
  value: {
    flex: 1,
    fontSize: 9,
    color: "#0f172a",
  },
  valueMono: {
    flex: 1,
    fontSize: 9,
    fontFamily: "Courier",
    color: "#0f172a",
  },
  // Used for the primary line inside a stacked cell (e.g. signer name when
  // org/title and email render below it). Same look as `value` but WITHOUT
  // `flex: 1` — flex on a Text inside a column-direction View makes it
  // stretch and overlap subsequent siblings under @react-pdf/renderer.
  valueStacked: {
    fontSize: 9,
    color: "#0f172a",
  },
  subValue: {
    fontSize: 8,
    color: "#475569",
    marginTop: 1,
  },
  disclosure: {
    marginTop: 12,
    paddingTop: 8,
    borderTopWidth: 0.5,
    borderTopColor: "#cbd5e1",
    fontSize: 7.5,
    color: "#475569",
    lineHeight: 1.45,
  },
});

function Row({ label, value, mono }) {
  if (value == null || value === "") return null;
  return (
    <View style={styles.row}>
      <Text style={styles.label}>{label}</Text>
      <Text style={mono ? styles.valueMono : styles.value}>{value}</Text>
    </View>
  );
}

// auditRecord shape (best-effort union across PSA / NAF / future TOU&BAA):
//   { signedBy, signedAt | signedDate, consentTimestamp,
//     pinVerified | pinHash, selfiePhotoBase64, selfieSkippedReason,
//     deviceMetadata: { timeZone, ... } }
//
// Any individual field may be absent; rows degrade gracefully to nothing.
export function SignatureCertificate({
  documentTitle,
  documentVersion,
  documentHash,
  signerName,
  signerEmail,
  signerTitle,
  organizationName,
  auditRecord,
}) {
  const signedAt = auditRecord?.signedAt || auditRecord?.signedDate || null;
  const sigId = signedAt && signerName ? shortSignatureId({ signedBy: signerName, signedDate: signedAt }) : null;
  // UTC only by design — local time + IANA timezone would leak the signer's
  // rough geographic region on the externally-shareable certificate. The
  // exact local time + timezone are still in the backend audit record and
  // recoverable by Signature ID for forensic / compliance review.
  const { utc: signedUtc } = formatUtcAndLocal(signedAt, null);
  const consentTs = auditRecord?.consentTimestamp || null;
  const { utc: consentUtc } = formatUtcAndLocal(consentTs, null);
  const elapsed = formatElapsed(consentTs, signedAt);

  // Whether this signature came through a flow that captures identity
  // attestations at all. Use any audit field as the signal — older surfaces
  // (legacy TOU/BAA wizard) only persist signedBy + signedDate, so there's
  // no audit context to attest to. For those, omit the Authentication
  // block entirely rather than print misleading "No"s.
  // The lightweight attestation booleans (pinVerified, selfieCaptured) are
  // the canonical post-sanitization signal — heavy fields like pinHash and
  // selfiePhotoBase64 get stripped from cached blobs (PSA) but the booleans
  // survive. Fall back to checking the heavy fields for backwards-compat
  // with NAF blobs that haven't been re-signed since this code shipped.
  const hasAuditContext =
    auditRecord != null &&
    (auditRecord.consentTimestamp != null ||
      auditRecord.pinVerified != null ||
      auditRecord.selfieCaptured != null ||
      auditRecord.selfiePhotoBase64 != null ||
      auditRecord.selfieSkippedReason != null ||
      auditRecord.pinHash != null ||
      auditRecord.deviceMetadata != null);

  // Explicit Yes/No so the audit record never omits whether the question
  // was even asked. Photo states:
  //   - Yes — selfieCaptured boolean true OR selfiePhotoBase64 present
  //   - No, declined — selfieSkippedReason captured
  //   - No, not captured — flow ran but no photo + no skip reason recorded
  const photoCaptured =
    auditRecord?.selfieCaptured === true || (auditRecord?.selfiePhotoBase64 != null && auditRecord?.selfiePhotoBase64 !== "");
  let identityPhotoLine = null;
  if (hasAuditContext) {
    if (photoCaptured) {
      identityPhotoLine = "Identity photo captured: Yes ✓";
    } else if (auditRecord?.selfieSkippedReason) {
      const reason = auditRecord.selfieSkippedReason === "no_camera" ? "no camera available" : "declined by signer";
      identityPhotoLine = `Identity photo captured: No (${reason})`;
    } else {
      identityPhotoLine = "Identity photo captured: No";
    }
  }

  // PIN attestation. NAF / PSA both surface this — modern records via the
  // pinVerified boolean (survives sanitization), older records via the
  // raw pinHash (stripped from cached PSA blob, still present on NAF).
  const pinVerified = auditRecord?.pinVerified === true || (auditRecord?.pinHash != null && auditRecord?.pinHash !== "");
  const pinLine = hasAuditContext ? `PIN verified: ${pinVerified ? "Yes ✓" : "No"}` : null;

  const docLine = [documentTitle, documentVersion ? `Version ${documentVersion}` : null].filter(Boolean).join(" • ") || null;
  const signerLine = signerName || null;
  const orgLine = [signerTitle, organizationName].filter(Boolean).join(" · ") || null;

  return (
    <View style={styles.cert} wrap={false}>
      <Text style={styles.title}>ELECTRONIC SIGNATURE CERTIFICATE</Text>

      <Row label="Document" value={docLine} />
      <Row label="Document hash" value={documentHash ? `SHA-256  ${abbreviateHash(documentHash)}` : null} mono />

      {signerLine ? (
        <View style={styles.row}>
          <Text style={styles.label}>Signer</Text>
          <View style={{ flex: 1 }}>
            <Text style={styles.valueStacked}>{signerLine}</Text>
            {orgLine ? <Text style={styles.subValue}>{orgLine}</Text> : null}
            {signerEmail ? <Text style={styles.subValue}>{signerEmail}</Text> : null}
          </View>
        </View>
      ) : null}

      <Row label="Signature ID" value={sigId ? `SIG-ID ${sigId}` : null} mono />
      <Row label="Signed (UTC)" value={signedUtc} />

      {consentUtc ? (
        <View style={styles.row}>
          <Text style={styles.label}>Consent accepted</Text>
          <View style={{ flex: 1 }}>
            <Text style={styles.valueStacked}>{consentUtc}</Text>
            {elapsed ? <Text style={styles.subValue}>{elapsed}</Text> : null}
          </View>
        </View>
      ) : null}

      {pinLine || identityPhotoLine ? (
        <View style={styles.row}>
          <Text style={styles.label}>Authentication</Text>
          <View style={{ flex: 1 }}>
            {pinLine ? <Text style={styles.valueStacked}>{pinLine}</Text> : null}
            {identityPhotoLine ? <Text style={styles.valueStacked}>{identityPhotoLine}</Text> : null}
          </View>
        </View>
      ) : null}

      <Text style={styles.disclosure}>
        This certificate documents the electronic signature placed on this agreement under the federal Electronic Signatures in
        Global and National Commerce Act (15 U.S.C. § 7001) and the Uniform Electronic Transactions Act. The full audit log —
        including network address, device fingerprint, geolocation, authentication credentials, and any identity-verification
        artifacts captured at signing — is retained by Lucas Health Corporation and is accessible by Signature ID upon legitimate
        audit, compliance, or legal request.
      </Text>
    </View>
  );
}

// NAF signature priority order. The form has up to four signed sections
// (BAA / PA / ACH / Credit Card); the certificate picks the first present
// blob as canonical and cites the rest in an "Other signatures" block so
// the cert page reads as one coherent attestation rather than four
// disjoint blocks.
const NAF_SIGNATURE_FIELDS = [
  { key: "By", label: "BAA (Covered Entity)" },
  { key: "pa_signature", label: "Purchase Agreement" },
  { key: "ach_signature", label: "ACH Authorization" },
  { key: "Cardholder Signature", label: "Credit Card Authorization" },
];

// Scans NAF fieldValues (or any equivalent {fieldName: jsonBlob} shape)
// for the four section signatures, returns `{ canonical, others }` where
// `canonical` is the highest-priority present blob and `others` is a list
// of `{ label, sigId, signedAt }` for the remaining signatures, suitable
// for `appendSignatureCertificatePage`'s `additionalSignatures` arg.
//
// `getSignatureBlob(fieldName)` is the caller's accessor — provider-side
// NAF translates through aliasMap (admin schema renames), manufacturer-side
// reads directly. Returns null when no signatures are present.
export function pickNafCanonicalSignature(getSignatureBlob) {
  const present = NAF_SIGNATURE_FIELDS.map((s) => ({
    ...s,
    info: getSignatureBlob(s.key),
  })).filter((s) => s.info);
  if (present.length === 0) return null;
  const [canonical, ...rest] = present;
  return {
    canonical,
    others: rest.map((s) => ({
      label: s.label,
      sigId: shortSignatureId({ signedBy: s.info.signedBy, signedDate: s.info.signedDate }),
      signedAt: s.info.signedDate,
    })),
  };
}

// Wraps <SignatureCertificate> in a standalone single-page PDF and copies
// it into an existing pdf-lib doc (mutated in place; appended as the last
// page). Used by the NAF generator, which builds the body in pdf-lib —
// merging avoids a second @react-pdf/renderer pass over the entire NAF.
//
// `additionalSignatures` is an optional list of `{ label, sigId, signedAt }`
// rendered as a "Other signatures on this document" block. Lets the NAF
// certificate cite all four section signatures (BAA, PA, ACH, CC) under
// one canonical certificate without rebuilding the layout per-signature.
export async function appendSignatureCertificatePage(
  pdfDoc,
  {
    documentTitle,
    documentVersion,
    documentHash,
    signerName,
    signerEmail,
    signerTitle,
    organizationName,
    auditRecord,
    additionalSignatures,
  }
) {
  const CertDoc = () => (
    <Document>
      <Page size="LETTER" style={{ padding: 50, fontFamily: "Helvetica" }}>
        <SignatureCertificate
          documentTitle={documentTitle}
          documentVersion={documentVersion}
          documentHash={documentHash}
          signerName={signerName}
          signerEmail={signerEmail}
          signerTitle={signerTitle}
          organizationName={organizationName}
          auditRecord={auditRecord}
        />
        {Array.isArray(additionalSignatures) && additionalSignatures.length > 0 ? (
          <View
            style={{
              marginTop: 16,
              padding: 12,
              borderTopWidth: 0.5,
              borderTopColor: "#cbd5e1",
            }}
          >
            <Text
              style={{
                fontSize: 9,
                fontFamily: "Helvetica-Bold",
                color: "#33415c",
                marginBottom: 6,
              }}
            >
              Other signatures on this document
            </Text>
            {additionalSignatures.map((sig, i) => (
              <View key={i} style={{ flexDirection: "row", marginBottom: 3 }}>
                <Text style={{ width: 110, fontSize: 9, color: "#33415c" }}>{sig.label}</Text>
                <Text style={{ flex: 1, fontSize: 9, fontFamily: "Courier", color: "#0f172a" }}>
                  {`SIG-ID ${sig.sigId}  •  ${sig.signedAt ? `${moment(sig.signedAt).utc().format("YYYY-MM-DD HH:mm:ss")} UTC` : "—"}`}
                </Text>
              </View>
            ))}
          </View>
        ) : null}
      </Page>
    </Document>
  );

  try {
    const blob = await pdf(<CertDoc />).toBlob();
    const bytes = await blob.arrayBuffer();
    const certDoc = await PDFDocument.load(bytes);
    const [certPage] = await pdfDoc.copyPages(certDoc, [0]);
    pdfDoc.addPage(certPage);
    return { ok: true };
  } catch (err) {
    // Always log so production failures land in browser consoles for support
    // triage. Returning the error lets the caller decide whether to show a
    // toast — utils shouldn't directly toast (would couple this module to
    // react-toastify and the calling component's i18n context).
    // eslint-disable-next-line no-console
    console.warn("[signatureCertificate] failed to append certificate page:", err);
    return { ok: false, error: err };
  }
}
